import { useReactTable } from '@tanstack/react-table';
import React from 'react';
import { match } from 'ts-pattern';

import { TooltipLabel } from '@eluve/blocks';
import {
  Box,
  ColDefBuilder,
  HStack,
  ServerDataTable,
  TableSearch,
  Tag,
  VStack,
  legacyTextStyles,
  useDataTableForQuery,
} from '@eluve/components';
import { PatientsOrderBy } from '@eluve/graphql-types';
import { ResultOf, graphql } from '@eluve/graphql.tada';
import { useAssignedTenantIdFromParams } from '@eluve/session-helpers';

const searchPatients = graphql(`
  query searchPatients(
    $filter: PatientsBoolExp
    $offset: Int
    $orderBy: [PatientsOrderBy!]
    $limit: Int
    $searchTerm: String
    $tenantId: uuid!
  ) {
    searchPatients(
      args: { search_term: $searchTerm, _tenant_id: $tenantId }
      where: $filter
      limit: $limit
      offset: $offset
      orderBy: $orderBy
    ) {
      __typename
      id
      firstName
      lastName
      dateOfBirth
      sex
      external_patients_info {
        __typename
        id
        externalPatientId
        external_ehr {
          id
          __typename
          vendor
        }
      }
      metrics {
        __typename
        id
        appointmentCount
        mostRecentAppointmentStartedAt
      }
    }
    searchPatientsAggregate(
      args: { search_term: $searchTerm, _tenant_id: $tenantId }
      where: $filter
    ) {
      __typename
      aggregate {
        __typename
        count
      }
    }
  }
`);

type Row = Omit<
  ResultOf<typeof searchPatients>['searchPatients'][number],
  'appointmentsAggregate' | 'metrics'
> & {
  importedFrom: React.ReactNode | null;
  appointmentCount: number;
  lastAppointmentDate: string | null;
};

const columns = new ColDefBuilder<Row>()
  .detailsLink((r) => `${r.id}`, 'Details')
  .defaultSortable('firstName', { label: 'First Name', isPrivate: true })
  .defaultSortable('lastName', { label: 'Last Name', isPrivate: true })
  .dateSortable('lastAppointmentDate', 'Last Appointment')
  .dateSortable('dateOfBirth', { label: 'Date of Birth', isPrivate: true })
  .defaultSortable('importedFrom', 'Imported From')
  .defaultSortable('appointmentCount', '# Appointments')
  .build();

const convertSearchResultsToRows = (
  results: ResultOf<typeof searchPatients>,
): Row[] => {
  return results.searchPatients.map((p) => {
    const { metrics, ...rest } = p;
    const externalPatientInfos = p.external_patients_info;
    const vendors = externalPatientInfos
      .filter((info) => info.external_ehr?.vendor)
      .map((info) => ({
        vendor: info.external_ehr.vendor,
        externalPatientId: info.externalPatientId,
      }));

    return {
      ...rest,
      importedFrom: vendors.length ? (
        <>
          {vendors.map(({ vendor, externalPatientId }) => (
            <HStack key={vendor} gap={1}>
              <TooltipLabel label="Copy Patient External ID">
                <Box
                  className="cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigator.clipboard.writeText(externalPatientId);
                  }}
                >
                  <Tag
                    color="gray"
                    label={vendor}
                    size="xs"
                    className={legacyTextStyles.body({
                      color: 'primary',
                      size: 's',
                    })}
                  />
                </Box>
              </TooltipLabel>
            </HStack>
          ))}
        </>
      ) : null,
      appointmentCount: metrics?.appointmentCount ?? 0,
      lastAppointmentDate: metrics?.mostRecentAppointmentStartedAt ?? null,
    };
  });
};

export const PatientsList: React.FC = () => {
  const tenantId = useAssignedTenantIdFromParams();
  const { data, rows, reactTableOptions, setSearch, search, isPending } =
    useDataTableForQuery({
      query: searchPatients,
      convertToRows: convertSearchResultsToRows,
      convertSearchParamsToVariables: ({ offset, limit, search, sorting }) => ({
        offset,
        limit,
        tenantId,
        searchTerm: search,
        orderBy:
          sorting && sorting.length
            ? sorting.map(({ desc, id }) => ({
                ...match<keyof Row, PatientsOrderBy>(id)
                  .with('lastAppointmentDate', () => ({
                    metrics: {
                      mostRecentAppointmentStartedAt: desc
                        ? 'DESC_NULLS_LAST'
                        : 'ASC',
                    },
                  }))
                  .with('appointmentCount', () => ({
                    metrics: {
                      appointmentCount: desc ? 'DESC_NULLS_LAST' : 'ASC',
                    },
                  }))
                  .with('importedFrom', () => ({
                    external_patients_infoAggregate: {
                      count: desc ? 'DESC_NULLS_LAST' : 'ASC',
                    },
                  }))
                  .otherwise(() => ({
                    [id]: desc ? 'DESC_NULLS_LAST' : 'ASC',
                  })),
              }))
            : null,
      }),
    });

  const rowCount = data?.searchPatientsAggregate?.aggregate?.count ?? 0;

  const table = useReactTable({
    data: rows,
    columns,
    rowCount,
    ...reactTableOptions,
  });

  return (
    <VStack gap={0} className="rounded-md border p-2">
      <TableSearch value={search ?? ''} onChange={setSearch} />
      <ServerDataTable<Row> table={table} isPending={isPending} />
    </VStack>
  );
};
